"use client";

import { useState } from "react";
import { KitCard, type KitCardData } from "../KitCard";
import { KitDetailsModal } from "../KitDetailsModal";
import * as styles from "./KitOptionsGrid.module.scss";

export interface KitOptionsGridProps {
	kits: KitCardData[];
}

/**
 * Client island for the kit-options section. Renders a responsive grid of
 * `KitCard`s and owns the "which kit is open" state so a single
 * `KitDetailsModal` can be reused for any card's "see what's included"
 * interaction.
 */
export function KitOptionsGrid({ kits }: KitOptionsGridProps) {
	const [openIndex, setOpenIndex] = useState<number | null>(null);

	if (kits.length === 0) return null;

	// Keep the modal mounted at all times (the DS Modal expects `isOpen` to
	// control visibility, mirroring the ImageGalleryModal precedent). Use the
	// first kit as a placeholder payload while closed — it's not visible.
	const modalKit = openIndex !== null ? kits[openIndex] : kits[0];

	return (
		<div
			className={styles["kit-options-grid"]}
			data-component="KitOptionsGrid"
		>
			<div className={styles["kit-options-grid__items"]}>
				{kits.map((kit, i) => (
					<KitCard
						key={
							kit.kit_product.sku ??
							`${kit.kit_product.title}-${i}`
						}
						title={kit.kit_product.title}
						badgeText={kit.badge_text}
						priceText={kit.price_text ?? kit.kit_product.price_text}
						marketingDescription={kit.marketing_description}
						image={kit.image ?? kit.kit_product.image}
						selectCta={kit.select_cta}
						viewDetailsLabel={kit.view_details_label}
						onViewDetails={() => setOpenIndex(i)}
					/>
				))}
			</div>

			<KitDetailsModal
				kit={{
					...modalKit.kit_product,
					// Fixtures sometimes put the kit image at the card level
					// rather than the product level — fall through so the
					// modal's left column always has an image when one exists.
					image: modalKit.kit_product.image ?? modalKit.image
				}}
				open={openIndex !== null}
				onClose={() => setOpenIndex(null)}
				selectHref={modalKit.select_cta?.href ?? "#"}
				selectLabel={modalKit.select_cta?.title ?? "Select this Kit"}
			/>
		</div>
	);
}
